import React, { useState } from 'react';
import axiosInstance from '../api/axiosInstance';
import { useNavigate } from 'react-router-dom';
import HCaptcha from '@hcaptcha/react-hcaptcha';

const Register = () => {
  const navigate = useNavigate();
  const [captchaToken, setCaptchaToken] = useState(null);
  const [message, setMessage] = useState(null);

  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const [agreed, setAgreed] = useState({
    privacy: false,
    rules: false
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaToken) {
      setMessage({ type: 'error', text: 'Bitte Captcha bestätigen.' });
      return;
    }

    if (!agreed.privacy || !agreed.rules) {
      setMessage({
        type: 'error',
        text: 'Bitte stimme den Forenregeln und der Datenschutzerklärung zu.'
      });
      return;
    }

    if (form.password !== form.confirmPassword) {
      setMessage({ type: 'error', text: 'Passwörter stimmen nicht überein.' });
      return;
    }

    try {
      await axiosInstance.post('/auth/register', {
        username: form.username,
        email: form.email,
        password: form.password,
        hcaptchaToken: captchaToken
      });

      setMessage({
        type: 'success',
        text: 'Registrierung erfolgreich! Bitte E-Mail bestätigen.'
      });

      setTimeout(() => navigate('/login'), 40000);
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.error || 'Registrierung fehlgeschlagen.'
      });
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <div className="w-full max-w-xl">
        <div className="modern-card p-8 animate-fade-in">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-2 text-primary">Werde Teil der Community!</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Erstelle dein Konto und diskutiere mit
            </p>
          </div>

          {message && (
            <div className={`alert mb-6 rounded-xl ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
              <div className="flex items-center gap-2">
                <span className="text-sm">{message.type === 'success' ? '✅' : '❌'}</span>
                <span>{message.text}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Benutzername
                </label>
                <input
                  type="text"
                  name="username"
                  value={form.username}
                  onChange={handleChange}
                  className="input input-bordered w-full rounded-xl border-gray-300 focus:border-primary focus:ring-primary/20 transition-all duration-200"
                  required
                  placeholder="z.B. MaxMuster"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  E-Mail
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className="input input-bordered w-full rounded-xl border-gray-300 focus:border-primary focus:ring-primary/20 transition-all duration-200"
                  required
                  placeholder="max@example.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Passwort
                </label>
                <input
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  className="input input-bordered w-full rounded-xl border-gray-300 focus:border-primary focus:ring-primary/20 transition-all duration-200"
                  required
                  placeholder="••••••••"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Passwort wiederholen
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  className="input input-bordered w-full rounded-xl border-gray-300 focus:border-primary focus:ring-primary/20 transition-all duration-200"
                  required
                  placeholder="••••••••"
                />
              </div>
            </div>

            <div className="space-y-4 mt-6">
              <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                <input
                  type="checkbox"
                  className="checkbox mt-1 checkbox-primary"
                  checked={agreed.privacy}
                  onChange={() => setAgreed(prev => ({ ...prev, privacy: !prev.privacy }))}
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Ich habe die{' '}
                  <a
                    href="/datenschutz"
                    className="text-primary hover:text-primary-600 underline font-medium"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Datenschutzerklärung
                  </a>{' '}
                  gelesen und stimme zu.
                </span>
              </div>

              <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                <input
                  type="checkbox"
                  className="checkbox mt-1 checkbox-primary"
                  checked={agreed.rules}
                  onChange={() => setAgreed(prev => ({ ...prev, rules: !prev.rules }))}
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Ich habe die{' '}
                  <a
                    href="/forenregeln"
                    className="text-primary hover:text-primary-600 underline font-medium"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Forenregeln
                  </a>{' '}
                  gelesen und stimme zu.
                </span>
              </div>
            </div>

            <div className="my-6 flex justify-center">
              <HCaptcha
                sitekey="20165f10-af5a-4fee-bcfe-fadcea18ad18"
                onVerify={setCaptchaToken}
                onExpire={() => setCaptchaToken(null)}
              />
            </div>

            <button 
              type="submit" 
              className="glass-btn w-full py-3 px-4 rounded-xl text-white font-semibold text-lg shadow-glow hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed" 
              disabled={!captchaToken}
            >
              Registrieren
            </button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-gray-600 dark:text-gray-400">
              Bereits ein Konto? {' '}
              <a href="/login" className="text-primary hover:text-primary-600 font-semibold transition-colors duration-200">
                Hier anmelden
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
